/* eslint-disable */
/// this component is used to importing and exporting components
/// simply wrap your component in the export function and make sure the export function is called from or in the main index file
/// to import just use the import component and make set the needed params.
/// the imported components will bind either right away, or as soon as their called component is ready.

///TL;DR
/// how it works.
/// At the current time there are 3 events, Bind, Bound, and Ready
/// bind:  this calls the component and has a node to attach itself to
/// bound: this is a call made by the component once it binds itself
/// ready: this is a call the component makes when it is loaded.

import { FC } from 'react';

import { buildIamTranslations } from '~utils/TranslationSetupUtils';

import { getActionName } from 'src/vulcan/common';

type TAddListenerProps = {
    eventName: string;
    listener: (event: CustomEvent) => void;
};

export type TExportedComponentProps<T> = {
    config: T;
    translations: {};
};

export type TBoundComponentProps = {
    componentName: string;
    config: {};
    hasLoyalty: boolean;
    translations: {};
    onBind?: (event: CustomEvent) => void;
    rootNode?: HTMLElement;
};

export type TExportExternal<T> = {
    componentName: string;
    Component: FC<TExportedComponentProps<T>>;
    onBind?: (event: CustomEvent) => void;
};

export const addListeners = ({ eventName, listener }: TAddListenerProps) => {
    addEventListener(eventName, listener);
    window.addEventListener(eventName, listener);
    window.document.addEventListener(eventName, listener);

    return () => {
        removeEventListener(eventName, listener);
        window.removeEventListener(eventName, listener);
        window.document.removeEventListener(eventName, listener);
    };
};

export const bindToDomElement = (props: TBoundComponentProps) => {
    const { config, translations, rootNode, onBind, componentName, hasLoyalty } = props;

    const detail = { config, translations, rootNode, hasLoyalty };
    const callToBind = new CustomEvent(getActionName.bind(componentName), { detail });
    const bindElement = (componentName: string) => {
        const boundEventName = getActionName.bound(componentName);

        dispatchEvent(callToBind);

        return addListeners({ eventName: boundEventName, listener: onBindListener });
    };

    const onBindListener = (event: CustomEvent) => {
        const { detail: incomingDetail } = event;
        if (incomingDetail === detail) {
            onBind?.(event);
        }
    };

    const bindOnReady = (componentName: string) => () => {
        const callback = () => dispatchEvent(callToBind);
        const eventName = getActionName['ready'](componentName);

        return addListeners({ eventName, listener: callback });
    };

    bindElement(componentName);
    bindOnReady(componentName);
};

export const getNodeByAttributeValue = (attr: string, attrValue: string) =>
    window.document.querySelector(`[${attr}=${attrValue}]`) as HTMLElement;

export const bindUsingDOM = ({
    attribute,
    attributeValue,
    externalProps
}: {
    attribute: string;
    attributeValue: string;
    externalProps: TExportExternal<any>;
}) => {
    const { componentName } = externalProps;

    const bindComponent = () => {
        const rootNode = getNodeByAttributeValue(attribute, attributeValue);
        // ?. is for avoiding sentry issue if vulcan cannot set config correctly
        //@ts-ignore
        const config = { ...window?.site?.config?.[componentName] };
        //@ts-ignore
        const hasLoyalty = window?.site?.config?.['hasLoyalty'];
        const translations = window?.site?.translations;

        if (rootNode) {
            bindToDomElement({
                config,
                hasLoyalty,
                translations: buildIamTranslations(translations),
                componentName,
                rootNode,
                onBind: () => {}
            });
            observer?.disconnect();
        }
    };

    const observer = new MutationObserver(bindComponent);

    try {
        const configs = { attributes: true, childList: true, subtree: true };
        observer.observe(window.document, configs);
    } catch (e) {
        console.log('failed to bind, trying again', { e });
        setTimeout(
            () =>
                bindUsingDOM({
                    attribute,
                    attributeValue,
                    externalProps
                }),
            100
        );
    }

    return null;
};
